import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Utils from 'common/services/Utils';
import { TopDriverCostDashboardDto, TopDriverCostsDashboardDto } from 'api/dashboard/dtos/TopDriverCostDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import useWidgetFilters from '../hooks/useWidgetFilters';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import useInfiniteReveal from '../components/useInfiniteReveal';
import KpiHeader from '../components/KpiHeader';
import { getPreviousRange, PreviousRange } from '../utils/getPreviousRange';
import styles from './TopDriverCosts.module.scss';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const PAGE_SIZE = 10;

const CHARGES_FILTERS_CACHE_KEY = 'CHARGESFILTER-0';

const SCALE_COLORS = [
	'#025a7a', // darkest — top rank
	'#038cb6',
	'#02c7c7',
	'#6de0e0',
	'#b8f0f0', // lightest — 5th+
];

const TopDriverCosts = () => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<TopDriverCostsDashboardDto | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [previousTotal, setPreviousTotal] = useState<number | null>(null);
	const [previousRange, setPreviousRange] = useState<PreviousRange | null>(null);
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('topDriverCosts');

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			setPreviousTotal(null);
			setPreviousRange(null);
			try {
				const result = await DashboardService.getTopDriverCosts({ dateFrom, dateTo });
				if (!cancelled) setData(result);
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}

			try {
				const range = getPreviousRange(dateFrom, dateTo);
				const previous = await DashboardService.getTopDriverCosts({ dateFrom: range.prevFrom, dateTo: range.prevTo });
				if (!cancelled) {
					setPreviousTotal(previous?.grandTotal ?? 0);
					setPreviousRange(range);
				}
			} catch {
				if (!cancelled) {
					setPreviousTotal(null);
					setPreviousRange(null);
				}
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [dateFrom, dateTo]);

	const handleRowClick = (item: TopDriverCostDashboardDto) => {
		try {
			const raw = localStorage.getItem(CHARGES_FILTERS_CACHE_KEY);
			const existing = raw ? JSON.parse(raw) : {};
			const next = {
				...existing,
				responsibleId: item.driverId,
				responsibleName: item.driverName,
				startDate: dateFrom,
				endDate: dateTo,
				page: 1,
				scrollToItemId: undefined,
			};
			localStorage.setItem(CHARGES_FILTERS_CACHE_KEY, JSON.stringify(next));
		} catch {
			// ignore
		}
		window.open('/charges', '_blank');
	};

	const goToAllCharges = () => {
		try {
			// Reset filters to match the widget's grand total — só período, sem condutor/tipo/etc.
			const next = {
				startDate: dateFrom,
				endDate: dateTo,
				vehiclesInactive: false,
			};
			localStorage.setItem(CHARGES_FILTERS_CACHE_KEY, JSON.stringify(next));
		} catch {
			// ignore
		}
		window.open('/charges', '_blank');
	};

	const items = data?.items || [];
	const maxCost = Math.max(...items.map(d => d.totalCost), 0);
	const grandTotal = data?.grandTotal ?? 0;
	const { visibleItems, sentinelRef, hasMore } = useInfiniteReveal(items, PAGE_SIZE);

	return (
		<Box className={tableStyles.container} title={t('dashboard.top_driver_costs') as string}
			subTitleTooltip={t('dashboard.top_driver_costs_tooltip' as any) as string}
			renderOptions={<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}>
			{isLoading ? (
				<div className={tableStyles.centerAlign}><LoadingSpinner /></div>
			) : !data || items.length === 0 ? (
				<WidgetNoData />
			) : (
				<>
					<KpiHeader
						value={Utils.formatCurrency(grandTotal)}
						subLabel={t('dashboard.total_cost' as any) as string}
						currentTotal={grandTotal}
						previousTotal={previousTotal}
						previousRange={previousRange}
						onClick={goToAllCharges}
					/>
					<div className={styles.list}>
					{visibleItems.map((item, index) => {
						const barWidth = maxCost > 0 ? (item.totalCost / maxCost) * 100 : 0;
						const color = SCALE_COLORS[Math.min(index, SCALE_COLORS.length - 1)];
						return (
							<div
								key={item.driverId}
								className={styles.row}
								onClick={() => handleRowClick(item)}
							>
								<span className={styles.name}>{item.driverName}</span>
								<div className={styles.barTrack}>
									<div className={styles.barFill} style={{ width: `${barWidth}%`, background: color }} />
								</div>
								<span className={`${styles.cost} ${index === 0 ? styles.costFirst : ''}`}>
									{Utils.formatCurrency(item.totalCost)}
								</span>
							</div>
						);
					})}
					{hasMore && <div ref={sentinelRef} className={infiniteStyles.sentinel} />}
					</div>
				</>
			)}
		</Box>
	);
};

export default TopDriverCosts;
